import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import {
  WhatsappLogo, Copy, CheckCircle, BellRinging, ArrowsClockwise, Calendar, CameraPlus,
  Pulse, Warning, ClockClockwise,
} from "@phosphor-icons/react";

const STATUS_META = {
  pending: { label: "Da inviare", color: "bg-[#F0F0EE] text-[#56635D]" },
  sent: { label: "Inviato", color: "bg-[#EEF3FF] text-[#3C5D8A]" },
  completed: { label: "Completato", color: "bg-[#EAFBEF] text-[#1F8B4C]" },
  missed: { label: "Non risposto", color: "bg-[#FAEBEB] text-[#A84A4A]" },
  risk_alert: { label: "Risk alert", color: "bg-[#FFF3E8] text-[#B45E2E]" },
};

const RISK_META = {
  green: { label: "Verde", color: "bg-[#EAFBEF] text-[#1F8B4C]" },
  yellow: { label: "Giallo", color: "bg-[#FFFAE9] text-[#8A6D1F]" },
  red: { label: "Rosso", color: "bg-[#FAEBEB] text-[#A84A4A]" },
};

export default function ScheduledFollowupsTab({ patient }) {
  const pid = patient.id;
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/patients/${pid}/scheduled-followups`);
      setItems(data || []);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const setBusyId = (id, k, v) => setBusy((b) => ({ ...b, [`${id}:${k}`]: v }));

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text || ""); } catch (_) {}
  };

  const markSent = async (item) => {
    setBusyId(item.id, "sent", true);
    try {
      await api.post(`/scheduled-followups/${item.id}/mark-sent`);
      await load();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusyId(item.id, "sent", false); }
  };

  const regenerate = async (item) => {
    setBusyId(item.id, "regen", true);
    try {
      await api.post(`/scheduled-followups/${item.id}/regenerate`);
      await load();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusyId(item.id, "regen", false); }
  };

  const remind = async (item) => {
    setBusyId(item.id, "remind", true);
    try {
      const { data } = await api.post(`/scheduled-followups/${item.id}/remind`);
      if (data?.reminder_message) {
        await copy(data.reminder_message);
      }
      await load();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusyId(item.id, "remind", false); }
  };

  const whatsappLink = (item) => {
    const phone = (patient.phone || "").replace(/\D/g, "");
    return phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(item.whatsapp_message || "")}`
      : `https://wa.me/?text=${encodeURIComponent(item.whatsapp_message || "")}`;
  };

  if (!patient.intervention_date) {
    return (
      <div className="elev-card p-6" data-testid="scheduled-no-date">
        <div className="text-sm text-[#56635D]">
          Imposta una <strong>data intervento</strong> nei Dettagli del paziente per generare automaticamente i 5 checkpoint di follow-up (giorno 1, 3, 7, 14, 30).
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="scheduled-followups-tab">
      <div className="elev-card p-4 flex items-start gap-3 bg-brand-50 border-brand-200">
        <Pulse size={20} weight="duotone" className="text-brand-700 mt-0.5 shrink-0" />
        <div className="text-sm text-[#1C2321]">
          <strong className="font-medium">Sistema di follow-up automatico.</strong>{" "}
          <span className="text-[#56635D]">5 checkpoint generati a partire dalla data intervento. I messaggi WhatsApp sono pronti per essere inviati dalla segreteria — niente invio automatico.</span>
        </div>
      </div>

      {error && <div className="text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2">{error}</div>}

      {items.length === 0 ? (
        <div className="elev-card p-6 text-center text-[#56635D]">Nessun follow-up programmato.</div>
      ) : (
        items.map((it) => {
          const s = STATUS_META[it.status] || STATUS_META.pending;
          const r = it.risk ? RISK_META[it.risk] : null;
          return (
            <div key={it.id} className="elev-card p-5" data-testid={`scheduled-${it.day}`}>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center font-heading font-semibold">
                  G{it.day}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-heading font-medium text-[#1C2321]">Controllo giorno {it.day}</span>
                    <span className="text-xs font-mono text-[#56635D] inline-flex items-center gap-1"><Calendar size={12} weight="duotone" /> {it.due_date}</span>
                    {it.photo_required && <span className="text-[10px] font-medium bg-[#FFF3E8] text-[#B45E2E] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><CameraPlus size={11} weight="duotone" /> foto richiesta</span>}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className={`text-[10px] font-medium px-2 py-1 rounded-full ${s.color}`}>{s.label}</span>
                    {r && <span className={`text-[10px] font-medium px-2 py-1 rounded-full ${r.color}`}>Rischio {r.label}</span>}
                    {it.reminded_at && <span className="text-[10px] font-medium px-2 py-1 rounded-full bg-[#F0F0EE] text-[#56635D] inline-flex items-center gap-1"><ClockClockwise size={11} weight="duotone" /> sollecitato</span>}
                  </div>
                </div>
              </div>

              {it.whatsapp_message && (
                <div className="bg-[#FAFAF8] border border-[#E6E8E6] rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-brand-400 mb-1.5">Messaggio WhatsApp</div>
                  <pre className="text-sm font-body text-[#1C2321] whitespace-pre-wrap leading-relaxed" data-testid={`scheduled-msg-${it.day}`}>{it.whatsapp_message}</pre>
                </div>
              )}

              {it.response_summary && (
                <div className="mt-3 text-xs text-[#56635D]">
                  <strong className="text-[#1C2321]">Risposta paziente:</strong> {it.response_summary}
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-4">
                <a href={whatsappLink(it)} target="_blank" rel="noreferrer" data-testid={`scheduled-open-wa-${it.day}`} className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebd5b] text-white px-3 py-2 rounded-lg text-sm">
                  <WhatsappLogo size={14} weight="fill" /> Apri WhatsApp
                </a>
                <button onClick={() => copy(it.whatsapp_message)} data-testid={`scheduled-copy-${it.day}`} className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-3 py-2 rounded-lg text-sm">
                  <Copy size={14} weight="duotone" /> Copia messaggio
                </button>
                {it.status === "pending" && (
                  <button onClick={() => markSent(it)} disabled={busy[`${it.id}:sent`]} data-testid={`scheduled-mark-sent-${it.day}`} className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-3 py-2 rounded-lg text-sm disabled:opacity-60">
                    <CheckCircle size={14} weight="duotone" /> Segna come inviato
                  </button>
                )}
                {(it.status === "sent" || it.status === "missed") && (
                  <button onClick={() => remind(it)} disabled={busy[`${it.id}:remind`]} data-testid={`scheduled-remind-${it.day}`} className="inline-flex items-center gap-2 bg-[#FFF3E8] text-[#B45E2E] hover:bg-[#FFE6D1] px-3 py-2 rounded-lg text-sm disabled:opacity-60">
                    <BellRinging size={14} weight="duotone" /> Sollecita paziente
                  </button>
                )}
                {it.status === "pending" && (
                  <button onClick={() => regenerate(it)} disabled={busy[`${it.id}:regen`]} className="inline-flex items-center gap-2 bg-[#F0F0EE] text-[#56635D] hover:bg-[#E6E8E6] px-3 py-2 rounded-lg text-sm disabled:opacity-60">
                    <ArrowsClockwise size={14} weight="duotone" /> Rigenera
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
